/**
 * Central date/time formatting (TIMEZONE-DISPLAY).
 *
 * Every timestamp the API returns is a UTC instant. Historically each call site
 * did its own `new Date(iso).toLocaleString()` — which silently localises to the
 * viewer's browser zone AND (almost everywhere) printed no zone label, so the
 * same session read as a different time to admins in different zones with no way
 * to tell which zone they were looking at.
 *
 * These helpers fix that: they always render from the UTC instant, and
 * `formatDateTime` always appends the zone label (e.g. "PDT" / "UTC"). Pass a
 * `timeZone` (an IANA name like "UTC") to force a specific zone — that's how the
 * per-tenant "show times in UTC" setting takes effect; omit it for the viewer's
 * local zone. Prefer the `useFormatDateTime` hook (util/displayTimeZone) so the
 * active tenant's setting is applied for you.
 */

export interface FormatOpts {
  /** IANA zone name (e.g. "UTC"); omit/empty = the viewer's local zone. */
  timeZone?: string | null;
}

function toDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Date + time, always zone-aware and always zone-LABELLED. Empty string on a
 * null/invalid input so callers can render it directly. */
export function formatDateTime(
  value: string | number | Date | null | undefined,
  opts: FormatOpts = {},
): string {
  const d = toDate(value);
  if (!d) return '';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: opts.timeZone || undefined,
    timeZoneName: 'short',
  });
}

/** Date only, still zone-aware (a UTC instant near midnight is a different day
 * in another zone) but unlabelled — dates don't usually carry a zone tag. */
export function formatDate(
  value: string | number | Date | null | undefined,
  opts: FormatOpts = {},
): string {
  const d = toDate(value);
  if (!d) return '';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: opts.timeZone || undefined,
  });
}

/**
 * Time to the SECOND (hh:mm:ss), zone-aware but NOT zone-labelled.
 *
 * For charts whose x-axis is a wall clock over minutes, where `formatTime`'s
 * minute precision is too coarse to tell two ticks apart (a 3-minute recording
 * printed "11:02 AM PDT" twice) and its zone suffix repeats on every tick.
 * The caller states the zone once — in an axis label or a caption — instead.
 */
export function formatTimeOfDay(
  value: string | number | Date | null | undefined,
  opts: FormatOpts = {},
): string {
  const d = toDate(value);
  if (!d) return '';
  return d.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: opts.timeZone || undefined,
  });
}

/** Time only (hh:mm), zone-aware and zone-labelled. */
export function formatTime(
  value: string | number | Date | null | undefined,
  opts: FormatOpts = {},
): string {
  const d = toDate(value);
  if (!d) return '';
  return d.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: opts.timeZone || undefined,
    timeZoneName: 'short',
  });
}
